import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { DataOrigin, RecordingMode } from "./dataset";
import type { MemberRef } from "./member";
import { invalidateScopedPolicyCatalog } from "./trainingPolicies";

/**
 * Dataset full configuration(설정)을 저장하는 경계다.
 *
 * 이름·담당자·행동 주체·수집 방식·embodiment·tip·label을 하나의 full target으로 저장한다.
 * 생성자나 admin 이 저장하면 바로 active 가 된다. 직전 active 는 1회 복원 후보(candidate)로 남는다.
 * 다른 member 는 검토 요청으로 후보만 올린다. 요청자가 어느 쪽인지는 서버가 `permissions` 로
 * 알려 준다. 축은 두 개뿐이다:
 * - active: 카탈로그에 반영된 설정
 * - candidate: Dataset 당 최대 1개인 후보 스냅샷
 * camera 채널은 이 계약에 포함되지 않는다. camera 채널은 업로드에서 도출하는 실측 인벤토리라서
 * write 대상이 아니다.
 *
 * 계약(backend `service/dataset_configuration/schema.py`)과 1:1 이다. 낙관적 동시성 토큰은
 * pair 전체의 `revision` 이다. active 나 candidate 가 실제로 바뀐 트랜잭션마다 1 증가한다.
 */

// ── 조회·응답 스냅샷 ─────────────────────────────────────────────────────

// active·candidate 스냅샷의 embodiment/tip/label 참조다. write payload 가 아니라 표시·diff 에 쓴다.
// 그래서 uuid 와 최소 표시 정보를 함께 담는다. 검증 근거는 uuid 와 현재 카탈로그 row 뿐이다.
export type ConfigEmbodimentRef = {
  uuid: string;
  name: string;
};

export type ConfigTipRef = {
  uuid: string;
  name: string;
  tag: string | null;
};

export type ConfigLabelRef = {
  uuid: string;
  key: string;
  value: string;
  // RGB 정수다(LabelResp.color 와 같음). 표시용 hex 변환은 c6 LabelChip 이 한다.
  color: number;
  // 생성 출처다. active.labels 에만 값이 있다. candidate 스냅샷 label 에서는 null 이다.
  // write payload 에는 이 필드가 없다.
  source: string | null;
};

// active 와 candidate.configuration 이 공유하는 설정 뷰다.
// 자동 null은 외부 출처에서는 해당 없음이고, 내부 출처에서는 미판정이다.
// 수동 null은 출처와 관계없이 명시적 해당 없음이다. 모든 자동 판정에서 제외한다.
export type ConfigRecordingMode = { value: RecordingMode | null; modified_manually: boolean };
export type ConfigRecordingModeTarget =
  | { modified_manually: false }
  | { modified_manually: true; value: RecordingMode | null };

export function recordingModeTarget(mode: ConfigRecordingMode): ConfigRecordingModeTarget {
  return mode.modified_manually
    ? { modified_manually: true, value: mode.value }
    : { modified_manually: false };
}

export function isRecordingModeNotApplicable(
  mode: ConfigRecordingMode,
  external: boolean,
): boolean {
  return mode.value === null && (mode.modified_manually || external);
}

export type ConfigurationView = {
  display_name: string;
  operator_member: MemberRef | null;
  data_origin: DataOrigin | null;
  recording_mode: ConfigRecordingMode;
  embodiment: ConfigEmbodimentRef | null;
  tip: ConfigTipRef | null;
  labels: readonly ConfigLabelRef[];
};

// 후보의 출처다. 사람이 올린 변경 요청이거나, 저장이 active 를 덮어쓰며 남긴 직전 값이다.
export type ConfigurationCandidateOrigin = "user_proposal" | "previous_active";

// Dataset 당 최대 1개인 후보 스냅샷이다.
export type ConfigCandidateView = {
  configuration: ConfigurationView;
  origin: ConfigurationCandidateOrigin;
  // 요청자다(uuid + 표시명).
  proposed_by: MemberRef | null;
  modified_at: string;
};

// candidate 기준으로 카탈로그에서 이미 삭제된 참조다(GET 시점에 검증). kind 는 대문자다.
export type UnresolvedReferenceKind = "Member" | "Embodiment" | "Tip" | "Label";

export type UnresolvedReference = {
  kind: UnresolvedReferenceKind;
  ref: MemberRef | ConfigEmbodimentRef | ConfigTipRef | ConfigLabelRef;
};

// 요청자 기준 쓰기 자격이다(서버가 계산).
//   - can_write: 저장·확정·폐기(생성자 또는 admin)
//   - can_propose: 검토 요청(모든 member)
// FE 는 등급 규칙을 복제하지 않는다. 이 값으로 버튼을 켠다.
export type DatasetConfigurationPermissions = {
  can_propose: boolean;
  can_write: boolean;
};

// GET/PUT/confirm/discard 의 공통 응답이다. 네 경로 모두 저장 후 최신 pair 를 반환한다.
// 그래서 다시 조회할 필요가 없다. 단 409 revision_conflict 는 pair 를 포함하지 않는다.
// 그 경우에는 GET 으로 다시 조회해야 한다.
export type DatasetConfigurationPair = {
  revision: number;
  permissions: DatasetConfigurationPermissions;
  active: ConfigurationView | null;
  candidate: ConfigCandidateView | null;
  unresolved_references: readonly UnresolvedReference[];
};

// ── 저장 요청(full target) ───────────────────────────────────────────────

// embodiment/tip 지정이다. uuid 만 보낸다(name/tag 는 계약 밖). null 은 embodiment 에서는
// sentinel unspecified, tip 에서는 없음을 뜻한다.
export type ConfigEntityTarget = {
  uuid: string;
};

// 라벨 target 이다. 아래 둘 중 정확히 하나를 보낸다:
//   - {label_uuid}: 기존 라벨을 지정한다.
//   - {key,value}: 서버가 strip().lower() 로 정규화한 뒤 get-or-create 한다.
// 둘 다 보내거나 둘 다 없으면 422 를 받는다. write payload 에 source 는 넣지 않는다.
export type ConfigLabelTarget = { label_uuid: string } | { key: string; value: string };

export type ConfigurationWriteView = {
  display_name: string;
  operator_member: ConfigEntityTarget | null;
  data_origin: DataOrigin | null;
  recording_mode: ConfigRecordingModeTarget;
  embodiment: ConfigEntityTarget | null;
  tip: ConfigEntityTarget | null;
  labels: readonly ConfigLabelTarget[];
};

// 요청·저장·확정의 공통 body 다. expected_revision 에는 직전 조회의 revision 을 그대로 보낸다
// (낙관적 동시성). 이 target 을 candidate 로 둘지 active 로 반영할지는 endpoint 가 정한다.
export type DatasetConfigurationPutReq = {
  expected_revision: number;
  configuration: ConfigurationWriteView;
};

// ── 엔드포인트 ───────────────────────────────────────────────────────────

// active + candidate pair 를 조회한다. 조직 멤버는 읽을 수 있다. 편집 UI 게이트는 응답의
// permissions 가 정한다.
export async function getDatasetConfiguration(
  datasetUuid: string,
): Promise<DatasetConfigurationPair> {
  const response = await apiClient.get<DatasetConfigurationPair>(
    apiRoutes.dataset.configuration(datasetUuid),
  );
  return response.data;
}

// full target 을 즉시 저장한다(생성자 또는 admin). 직전 active 는 candidate(previous_active)가
// 되어 기존 검토 요청을 대체한다. 그 후보 때문에 Dataset 이 리뷰 큐에 들어간다. 응답은 최신 pair 다.
export async function putDatasetConfiguration(
  datasetUuid: string,
  payload: DatasetConfigurationPutReq,
): Promise<DatasetConfigurationPair> {
  const response = await apiClient.put<DatasetConfigurationPair>(
    apiRoutes.dataset.configuration(datasetUuid),
    payload,
  );
  invalidateScopedPolicyCatalog();
  return response.data;
}

// full target 을 변경 요청으로 올린다(모든 member). active 는 바꾸지 않고 candidate 만 갱신한다.
// Dataset 당 candidate 는 하나라서 새 요청이 이전 요청을 대체한다. 응답은 최신 pair 다.
export async function proposeDatasetConfiguration(
  datasetUuid: string,
  payload: DatasetConfigurationPutReq,
): Promise<DatasetConfigurationPair> {
  const response = await apiClient.put<DatasetConfigurationPair>(
    apiRoutes.dataset.configurationCandidate(datasetUuid),
    payload,
  );
  return response.data;
}

// FE 가 조립한 target 으로 candidate 를 확정한다(생성자 또는 admin). 서버는 한 트랜잭션에서
// 아래 순서로 처리한다:
//   1. 다시 검증한다.
//   2. active 에 적용한다.
//   3. candidate 를 삭제한다.
//   4. 리뷰 큐에서 뺀다(`reviewed=true`).
// body 는 요청·저장과 같다.
export async function confirmDatasetConfigurationCandidate(
  datasetUuid: string,
  payload: DatasetConfigurationPutReq,
): Promise<DatasetConfigurationPair> {
  const response = await apiClient.post<DatasetConfigurationPair>(
    apiRoutes.dataset.configurationCandidateConfirm(datasetUuid),
    payload,
  );
  invalidateScopedPolicyCatalog();
  return response.data;
}

// candidate 를 폐기한다(생성자 또는 admin, body 없음). active 는 바꾸지 않고 candidate 만 즉시
// 삭제한다. 되돌릴 수 없는 동작이다.
export async function discardDatasetConfigurationCandidate(
  datasetUuid: string,
): Promise<DatasetConfigurationPair> {
  const response = await apiClient.post<DatasetConfigurationPair>(
    apiRoutes.dataset.configurationCandidateDiscard(datasetUuid),
  );
  return response.data;
}

// 후보가 사람이 올린 검토 요청인지 판정한다. 호출부는 이 값으로 폼을 후보 값으로 채울지 정한다.
// 저장이 남긴 직전 값(previous_active)은 요청이 아니라 되돌리기용 값이다. 이 값으로 폼을 채우면
// 방금 저장한 값이 화면에서 되돌아간다.
export function isUserProposal(
  candidate: ConfigCandidateView | null,
): candidate is ConfigCandidateView {
  return candidate?.origin === "user_proposal";
}

// ── 에러 판별 ────────────────────────────────────────────────────────────
// backend 는 dataset_configuration.* 네임스페이스의 인라인 리터럴로 에러를 던진다
// (_codes.py enum 아님). 판별자는 코드 문자열을 정확히 매칭한다.

// 다른 사용자가 먼저 저장했다(409). 서버에 부수효과는 없다. 처리 방법은 GET 으로 다시 조회한 뒤
// 비교 mode 로 전환하는 것이다. 자동 재시도나 병합은 하지 않는다. 409 응답에는 최신 pair 가 없다.
export function isRevisionConflict(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "dataset_configuration.revision_conflict";
}

// 지정한 카탈로그 row 가 삭제되었다(409). 복원 중 경합으로 row 가 사라진 경우다.
export function isUnresolvedReferencesError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "dataset_configuration.unresolved_references";
}

// 정규화 후 라벨이 중복된다(422). 입력 검증이 먼저 막지만, 최종 판정은 서버가 한다.
export function isLabelDuplicateError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "dataset_configuration.label_duplicate";
}
